import React from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import IconResolver from "../../../../shared/components/common/ui/IconResolver";
import {
  dispatchErrorSx,
  dispatchFiltersSx,
  dispatchTableCardSx,
  dispatchTableSx,
  refreshButtonSx,
  sectionSubtitleSx,
} from "../CampaignViewPage.styles";
import {
  dispatchChannelOptions,
  dispatchStatusOptions,
  dispatchTableLocaleText,
} from "../CampaignViewPage.utils";

export default function CampaignDispatchSection({
  dispatchDraftFilters,
  dispatchRows,
  dispatchColumns,
  dispatchRowCount,
  dispatchLoading,
  dispatchError,
  dispatchPaginationModel,
  onDraftFiltersChange,
  onApplyFilters,
  onClearFilters,
  onReload,
  onPaginationModelChange,
}) {
  const renderSelectedValues = (selectedValues, options) => {
    if (!selectedValues.length) {
      return "Todos";
    }

    return options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label)
      .join(", ");
  };

  const renderSkeletonRows = () => (
    <Box
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "90px 1.1fr 1.4fr 1.2fr 120px",
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        {["Canal", "Data", "Destinatário", "Status", "Ações"].map((item) => (
          <Typography
            key={item}
            sx={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}
          >
            {item}
          </Typography>
        ))}
      </Box>

      {Array.from({ length: Math.min(dispatchPaginationModel.pageSize || 10, 10) }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: "grid",
            gridTemplateColumns: "90px 1.1fr 1.4fr 1.2fr 120px",
            gap: 1,
            alignItems: "center",
            px: 1.5,
            py: 1,
            borderBottom: index === Math.min(dispatchPaginationModel.pageSize || 10, 10) - 1
              ? "none"
              : "1px solid #eef2f7",
          }}
        >
          <Skeleton variant="circular" width={22} height={22} />
          <Skeleton variant="text" width="82%" height={24} />
          <Skeleton variant="text" width="92%" height={24} />
          <Skeleton variant="rounded" width={118} height={24} sx={{ borderRadius: "6px" }} />
          <Skeleton variant="rounded" width={94} height={28} sx={{ borderRadius: "999px", justifySelf: "end" }} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Paper sx={dispatchTableCardSx}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        mb={2}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
            Disparos
          </Typography>
          <Typography sx={sectionSubtitleSx}>Veja todos os disparos</Typography>
        </Box>

        <IconButton
          onClick={onReload}
          disabled={dispatchLoading}
          sx={refreshButtonSx}
        >
          <IconResolver name="refresh" />
        </IconButton>
      </Stack>

      <Box sx={dispatchFiltersSx}>
        <TextField
          select
          size="small"
          label="Status"
          value={dispatchDraftFilters.status}
          onChange={(event) => onDraftFiltersChange("status", event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) =>
              renderSelectedValues(selected, dispatchStatusOptions),
          }}
        >
          {dispatchStatusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                size="small"
                checked={dispatchDraftFilters.status.includes(option.value)}
              />
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Canal"
          value={dispatchDraftFilters.channel}
          onChange={(event) => onDraftFiltersChange("channel", event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) =>
              renderSelectedValues(selected, dispatchChannelOptions),
          }}
        >
          {dispatchChannelOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                size="small"
                checked={dispatchDraftFilters.channel.includes(option.value)}
              />
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="Data início"
          type="date"
          value={dispatchDraftFilters.startDate}
          onChange={(event) =>
            onDraftFiltersChange("startDate", event.target.value)
          }
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small"
          label="Data fim"
          type="date"
          value={dispatchDraftFilters.endDate}
          onChange={(event) => onDraftFiltersChange("endDate", event.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button variant="contained" onClick={onApplyFilters} sx={{ height: 40 }}>
          Aplicar
        </Button>

        <Button variant="text" onClick={onClearFilters} sx={{ height: 40 }}>
          Limpar
        </Button>
      </Box>

      {dispatchError ? (
        <Box sx={dispatchErrorSx}>
          <Typography color="error">{dispatchError}</Typography>
        </Box>
      ) : dispatchLoading ? (
        renderSkeletonRows()
      ) : (
        <DataGrid
          autoHeight
          rows={dispatchRows}
          columns={dispatchColumns}
          rowCount={dispatchRowCount}
          loading={false}
          pagination
          paginationMode="server"
          paginationModel={dispatchPaginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[50]}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          sx={dispatchTableSx}
          localeText={dispatchTableLocaleText}
        />
      )}
    </Paper>
  );
}
